import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Divider,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  MenuProps,
} from "@mui/material";

import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { DateTime } from "./primitives/DateTime";
import { PatientData } from "../models/patient-data";
import { useAllPatientData, useDeletePatient } from "../queries/patients-query";
import { useAppContext } from "../context/AppContext";
import { PatientExportsMenu } from "./patient-exports";

export const PatientMenu: React.FC<Omit<MenuProps, "open">> = ({
  ...menuProps
}) => {
  const { t } = useTranslation();

  const { mutateAsync: deletePatient } = useDeletePatient();

  const {
    dataConfig,
    patientMenu,
    setDataConfig,
    togglePatientDialog,
    togglePatientMenu,
  } = useAppContext();

  const { data: exports } = useAllPatientData(patientMenu?.patient?.id);

  const [dialog, setDialog] = useState<boolean>(false);

  const onMenuClose = useCallback(() => {
    togglePatientMenu();
  }, [togglePatientMenu]);

  const onExportClick = useCallback(
    (exportId: PatientData["exportId"]) => () => {
      togglePatientMenu();
      setDataConfig({ patientId: patientMenu?.patient?.id, exportId });
    },
    [patientMenu?.patient, setDataConfig, togglePatientMenu],
  );

  const onAttemptNewImport = useCallback(() => {
    togglePatientMenu();
    setDataConfig((s) => ({ ...s, exportId: null }));
  }, [setDataConfig, togglePatientMenu]);

  const onEdit = useCallback(() => {
    togglePatientMenu();
    togglePatientDialog({ patient: patientMenu?.patient });
  }, [togglePatientMenu, togglePatientDialog, patientMenu?.patient]);

  const onDelete = () => setDialog(true);

  const onDeleteCancel = () => {
    setDialog(false);
    togglePatientMenu();
  };

  const onDeleteConfirm = useCallback(() => {
    if (!patientMenu?.patient) return;
    togglePatientMenu();
    setDialog(false);
    deletePatient(patientMenu?.patient);
  }, [patientMenu?.patient, deletePatient, togglePatientMenu]);

  const exportsElements = useMemo(() => {
    return exports
      ?.sort(
        (a: PatientData, b: PatientData) =>
          b.exportDate.getTime() - a.exportDate.getTime(),
      )
      .map((patientExport: PatientData) => (
        <MenuItem
          component={ListItem}
          key={patientExport.exportId}
          onClick={onExportClick(patientExport.exportId)}
          secondaryAction={
            patientMenu?.patient ? (
              <PatientExportsMenu
                patient={patientMenu.patient}
                id={patientExport.exportId}
                date={format(patientExport.exportDate, "yyyy-MM-dd HH:mm")}
              />
            ) : null
          }
          selected={dataConfig.exportId === patientExport.exportId}
          sx={{ pr: 8 }}
        >
          <ListItemText
            primary={
              <DateTime timestamp={patientExport.exportDate.getTime()} />
            }
            primaryTypographyProps={{
              component: "div",
              style: { fontVariantNumeric: "tabular-nums" },
              variant: "body2",
            }}
          />
        </MenuItem>
      ));
  }, [dataConfig.exportId, exports, onExportClick, patientMenu?.patient]);

  return (
    <>
      <Menu
        anchorEl={patientMenu?.anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={onMenuClose}
        open={!!patientMenu?.anchorEl && !!patientMenu?.patient}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
        MenuListProps={{ dense: true, component: "div" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        {...menuProps}
      >
        {patientMenu?.displayImports && (
          <>
            <List
              dense
              subheader={
                <ListSubheader>{t("patient.imports-header")}</ListSubheader>
              }
            >
              {exportsElements}
              {exports &&
                exports?.length > 0 &&
                patientMenu?.displayImports && (
                  <MenuItem
                    disabled={exports.length < 1}
                    onClick={onAttemptNewImport}
                  >
                    <ListItemText
                      primary={t("patient.import-cta")}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </MenuItem>
                )}
            </List>
            <Divider />
          </>
        )}
        {patientMenu?.patient && (
          <List
            dense
            subheader={
              patientMenu?.displayImports && (
                <ListSubheader>{t("patient.manage-header")}</ListSubheader>
              )
            }
          >
            <MenuItem onClick={onEdit}>{t("common.edit")}</MenuItem>
            <MenuItem onClick={onDelete} sx={{ color: "error.main" }}>
              {t("common.delete")}
            </MenuItem>
          </List>
        )}
      </Menu>
      <ConfirmDeleteDialog
        dialogMessage={t("patient.delete-text", {
          patient: patientMenu?.patient?.name,
        })}
        dialogTitle={t("patient.delete-title")}
        onConfirm={onDeleteConfirm}
        onDiscard={onDeleteCancel}
        open={dialog}
      />
    </>
  );
};
